class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.clearConfirmation) {
        evt.stopPropagation();
        this.dismissClearConfirmation();
      } else this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-samago-cart-clear]');
      if (button) this.requestClearConfirmation(button);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.returnPosition) this.captureReturnPosition();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.dismissClearConfirmation(false);
    this.classList.remove('active');
    removeTrapFocus();
    document.body.classList.remove('overflow-hidden');
    const returnTarget = this.activeElement?.isConnected
      ? this.activeElement
      : document.querySelector('#cart-icon-bubble');
    returnTarget?.focus({ preventScroll: true });
    if (this.returnPosition) window.scrollTo({ ...this.returnPosition, behavior: 'instant' });
    this.returnPosition = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    this.dismissClearConfirmation(false);
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
    if (!this.classList.contains('active')) this.captureReturnPosition();
  }

  captureReturnPosition() {
    this.returnPosition = { left: window.scrollX, top: window.scrollY };
  }

  requestClearConfirmation(button) {
    if (this.clearing || this.clearConfirmation || this.querySelector('.cart__items--disabled')) return;
    const inner = this.querySelector('.drawer__inner');
    const overlay = document.createElement('div');
    overlay.className = 'samago-cart-confirm';
    overlay.innerHTML = `
      <div class="samago-cart-confirm__panel" role="alertdialog" aria-modal="true"
        aria-labelledby="SamagoClearTitle" aria-describedby="SamagoClearDescription" tabindex="-1">
        <h2 id="SamagoClearTitle">¿Querés vaciar el carrito?</h2>
        <p id="SamagoClearDescription">Se quitarán todos los productos.</p>
        <div class="samago-cart-confirm__actions">
          <button type="button" class="button button--secondary" data-clear-cancel>Cancelar</button>
          <button type="button" class="button" data-clear-confirm>Vaciar carrito</button>
        </div>
      </div>`;
    const background = [...inner.children].map((element) => ({ element, inert: element.inert }));
    background.forEach(({ element }) => { element.inert = true; });
    inner.appendChild(overlay);
    this.clearConfirmation = { overlay, background, button };
    overlay.querySelector('[data-clear-cancel]').addEventListener('click', () => this.dismissClearConfirmation());
    overlay.querySelector('[data-clear-confirm]').addEventListener('click', () => {
      this.dismissClearConfirmation();
      this.clearCart(button);
    });
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissClearConfirmation();
    });
    trapFocus(overlay.querySelector('[role="alertdialog"]'), overlay.querySelector('[data-clear-cancel]'));
  }

  dismissClearConfirmation(restoreFocus = true) {
    if (!this.clearConfirmation) return;
    const { overlay, background, button } = this.clearConfirmation;
    this.clearConfirmation = null;
    overlay.remove();
    background.forEach(({ element, inert }) => { element.inert = inert; });
    removeTrapFocus();
    if (restoreFocus && this.classList.contains('active')) {
      trapFocus(this.querySelector('.drawer__inner'), button.isConnected ? button : this.querySelector('.drawer__close'));
    }
  }

  async clearCart(button) {
    if (this.clearing || this.querySelector('.cart__items--disabled')) return;
    this.clearing = true;
    const controls = [...this.querySelectorAll('button, input')].filter((control) => !control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    let cleared = false;
    try {
      const pageItems = document.querySelector('cart-items');
      const pageSections = pageItems ? ['main-cart-items', 'main-cart-footer'].map((id) => document.getElementById(id)).filter(Boolean) : [];
      const sectionIds = [...this.getSectionsToRender().map((section) => section.id), ...pageSections.map((element) => element.dataset.id)];
      const response = await fetch(`${window.routes.cart_url}/clear.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: sectionIds, sections_url: window.location.pathname + window.location.search }),
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.item_count !== 0) throw new Error('clear failed');
      cleared = true;
      if (!sectionIds.every((id) => cart.sections?.[id])) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionIds.join(','));
        const sectionsResponse = await fetch(url, { credentials: 'same-origin' });
        if (!sectionsResponse.ok) throw new Error('sections failed');
        cart.sections = await sectionsResponse.json();
      }
      if (!sectionIds.every((id) => cart.sections?.[id])) throw new Error('sections missing');
      // Validate all fragments before replacing the drawer that contains the error message.
      this.getSectionsToRender().forEach((section) => {
        this.getSectionInnerHTML(cart.sections[section.id], section.selector);
      });
      const pageContents = pageSections.map((element) => this.getSectionInnerHTML(cart.sections[element.dataset.id], '.js-contents'));
      this.renderContents(cart, { open: false });
      pageSections.forEach((element, index) => {
        const contents = element.querySelector('.js-contents');
        if (contents) contents.innerHTML = pageContents[index];
        element.classList.add('is-empty');
      });
      pageItems?.classList.add('is-empty');
      if (this.classList.contains('active')) {
        trapFocus(this.querySelector('.drawer__inner-empty'), this.querySelector('.samago-cart-empty__cta'));
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
      document.dispatchEvent(new CustomEvent('samago:cart:changed', { detail: { cart, quantity: 0 } }));
    } catch (error) {
      if (errors) errors.textContent = cleared
        ? 'El carrito se vació, pero no pudimos actualizar la vista. Volvé a intentarlo para actualizarla.'
        : 'No pudimos vaciar el carrito. Volvé a intentarlo.';
    } finally {
      this.clearing = false;
      this.removeAttribute('aria-busy');
      controls.forEach((control) => { if (control.isConnected) control.disabled = false; });
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
